import { usePayeeDetailDrawer } from '@/features/ap_payees/components/PayeeDetailDrawer/context/PayeeDetailDrawerProvider';
import { useScrollToSectionOnEdit } from '@/features/ap_payees/components/PayeeDetailDrawer/hooks/useScrollToSectionOnEdit';
import {
  AnchorItemKey,
  RECONCILIATION_DEFAULT_STATUS,
  RECONCILIATION_STATUS_LABEL_KEYS,
  ReconciliationSettingData,
  SECTION_KEYS,
} from '@/features/ap_payees/components/PayeeDetailDrawer/types';
import { useTranslation } from '@/i18n';
import { ButtonV2 } from '@moneyforward/ap-frontend-components';
import classnames from 'classnames/bind';
import { FC, useCallback, useMemo } from 'react';
import styles from '../../styles.module.scss';
import { HeaderSection, SingleRow } from '../Common';
import { Edit } from './Edit';

const cx = classnames.bind(styles);

type ReconciliationSettingProps = {
  data?: ReconciliationSettingData | undefined;
  isEditable?: boolean;
};

export const ReconciliationSetting: FC<ReconciliationSettingProps> = ({
  data,
  isEditable = false,
}) => {
  const { t } = useTranslation();
  const { isEditingSection, setIsEditingSection } = usePayeeDetailDrawer();
  const isEditing = isEditingSection === SECTION_KEYS.RECONCILIATION;
  const sectionRef = useScrollToSectionOnEdit(
    isEditing,
    SECTION_KEYS.RECONCILIATION
  );

  const setting = data?.ap_payee_reconciliation_setting;

  const displayValue = useMemo(() => {
    const status = setting?.status ?? RECONCILIATION_DEFAULT_STATUS;

    return t(RECONCILIATION_STATUS_LABEL_KEYS[status]);
  }, [setting?.status, t]);

  const onActionButtonClick = useCallback(
    (e?: React.MouseEvent) => {
      e?.preventDefault();
      e?.stopPropagation();
      setIsEditingSection(SECTION_KEYS.RECONCILIATION);
    },
    [setIsEditingSection]
  );

  // Hidden while the office has not enabled 突合サービス連携 — the BE returns null
  // for `ap_payee_reconciliation_setting` in that case.
  if (!setting) {
    return null;
  }

  if (isEditing) {
    return (
      <div
        ref={sectionRef}
        id={AnchorItemKey.RECONCILIATION}
        className={cx('sectionContainer')}
      >
        <Edit data={data} />
      </div>
    );
  }

  return (
    <div
      ref={sectionRef}
      id={AnchorItemKey.RECONCILIATION}
      className={cx('sectionContainer')}
    >
      <div className={cx('reconciliationHeaderRow')}>
        <div className={cx('reconciliationHeaderContent')}>
          <HeaderSection displayName={t('payee_reconciliation_setting')} />
          <div className={cx('sectionDescription')}>
            {t('payee_reconciliation_setting_description')}
          </div>
        </div>
        {isEditable && (
          <ButtonV2
            onClick={onActionButtonClick}
            isSecondary
            isDisabled={isEditingSection !== SECTION_KEYS.NULL}
          >
            {t('edit')}
          </ButtonV2>
        )}
      </div>
      <div className={cx('bodySectionContainer')}>
        <SingleRow
          label={t('payee_reconciliation_setting_when_receive_invoice')}
          value={displayValue}
        />
      </div>
    </div>
  );
};

ReconciliationSetting.displayName = 'ReconciliationSetting';
